import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { useQuoting } from '../hooks/useQuoting';
import { getQuoteSettings, saveQuoteSettings } from '../utils/quotes';

const TIMING_KINDS = [
  ['immediate', 'Immediate'],
  ['current_season', 'Current season'],
  ['next_season', 'Next season'],
  ['event', 'Event date']
];

function optionFor(key, index) {
  const base = {
    id: `custom_${key.toLowerCase()}_${Date.now().toString(36)}_${index}`,
    label: 'New option'
  };
  if (key === 'timing') return { ...base, kind: 'immediate', days: 30 };
  if (key === 'budget') return { ...base, min: 0, max: 1000 };
  if (key === 'productFit') return { ...base, group: 'category', keywords: ['new'] };
  return base;
}

export default function QuoteSettingsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const quoting = useQuoting(currentUser);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!quoting.loaded || !quoting.addonEnabled) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    getQuoteSettings().then(result => {
      if (active) setSettings(result.settings);
    }).catch(err => {
      if (active) setError(err.message || 'Unable to load quote settings.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [quoting.addonEnabled, quoting.loaded]);

  const updateQuestion = (key, update) => {
    setSettings(current => ({
      ...current,
      questions: {
        ...current.questions,
        [key]: { ...current.questions[key], ...update }
      }
    }));
  };

  const updateOption = (key, index, update) => {
    const options = settings.questions[key].options.map((option, optionIndex) =>
      optionIndex === index ? { ...option, ...update } : option);
    updateQuestion(key, { options });
  };

  const removeOption = (key, index) => {
    updateQuestion(key, {
      options: settings.questions[key].options.filter((_, i) => i !== index)
    });
  };

  const addOption = key => {
    const options = [...settings.questions[key].options, optionFor(key, settings.questions[key].options.length)];
    updateQuestion(key, { options });
  };

  const updateSeason = (index, update) => {
    const seasons = settings.seasonPolicy.seasons.map((season, i) =>
      i === index ? { ...season, ...update } : season);
    setSettings(current => ({
      ...current,
      seasonPolicy: { ...current.seasonPolicy, seasons }
    }));
  };

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await saveQuoteSettings(settings);
      setSettings(result.settings);
      setMessage('Quote settings saved and audited.');
    } catch (err) {
      setError(err.message || 'Unable to save quote settings.');
    } finally {
      setSaving(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return <main className="p-6"><p role="alert" className="rounded bg-red-50 p-4 text-red-800">Only tenant admins can manage quote questions and choices.</p></main>;
  }

  if (!quoting.loaded) return <main className="p-6">Checking add-on access…</main>;

  if (!quoting.addonEnabled) {
    return <main className="p-6"><p className="rounded bg-gray-100 p-4">The AI Sales Agent add-on is not enabled for this tenant.</p></main>;
  }

  if (loading) return <main className="p-6">Loading quote settings…</main>;
  if (!settings) return <main className="space-y-3 p-6">
    {error && <p role="alert" className="rounded bg-red-50 p-4 text-red-800">{error}</p>}
    <button type="button" onClick={() => navigate('/admin')} className="text-indigo-700 underline">Back to Administration</button>
  </main>;

  return (
    <main className="mx-auto max-w-5xl space-y-5 p-3 md:p-6">
      <div>
        <button type="button" onClick={() => navigate('/admin')} className="mb-2 text-sm text-indigo-700 underline">Back to Administration</button>
        <h1 className="text-2xl font-bold text-gray-900">Quote Builder Settings</h1>
        <p className="text-sm text-gray-600">Configure the question choices reps see. These choices do not enable quoting or paid AI; those are managed separately by your CRM provider.</p>
      </div>
      {quoting.error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{quoting.error}</p>}
      {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p>}
      {message && <p role="status" className="rounded bg-green-50 p-3 text-green-800">{message}</p>}
      <form onSubmit={save} className="space-y-5">
        {['need', 'timing', 'budget', 'productFit'].map(key => {
          const question = settings.questions[key];
          return (
            <section key={key} className="space-y-3 rounded-lg border bg-white p-4">
              <label className="block text-sm font-semibold">
                Question
                <input value={question.label}
                  onChange={event => updateQuestion(key, { label: event.target.value })}
                  maxLength={80} className="mt-1 block w-full rounded border p-2" />
              </label>
              {(key === 'need' || key === 'productFit') && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={question.multi}
                    onChange={event => updateQuestion(key, { multi: event.target.checked })} />
                  Allow reps to select multiple choices
                </label>
              )}
              <div className="space-y-3">
                {question.options.map((option, index) => (
                  <div key={option.id} className="grid grid-cols-1 items-end gap-2 rounded border p-3 sm:grid-cols-2 lg:grid-cols-4">
                    <label className="text-sm">Button label
                      <input value={option.label}
                        onChange={event => updateOption(key, index, { label: event.target.value })}
                        maxLength={60} className="mt-1 block w-full rounded border p-2" />
                    </label>
                    {key === 'timing' && (
                      <label className="text-sm">Window behavior
                        <select value={option.kind}
                          onChange={event => updateOption(key, index, { kind: event.target.value })}
                          className="mt-1 block w-full rounded border p-2">
                          {TIMING_KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </label>
                    )}
                    {key === 'timing' && option.kind === 'immediate' && (
                      <label className="text-sm">Days covered
                        <input type="number" min="1" max="365" value={option.days || 30}
                          onChange={event => updateOption(key, index, { days: Number(event.target.value) })}
                          className="mt-1 block w-full rounded border p-2" />
                      </label>
                    )}
                    {key === 'budget' && (
                      <>
                        <label className="text-sm">Minimum ($)
                          <input type="number" min="0" max="10000000" value={option.min}
                            onChange={event => updateOption(key, index, { min: Number(event.target.value) })}
                            className="mt-1 block w-full rounded border p-2" />
                        </label>
                        <label className="text-sm">Maximum ($)
                          <input type="number" min="1" max="10000000" value={option.max}
                            onChange={event => updateOption(key, index, { max: Number(event.target.value) })}
                            className="mt-1 block w-full rounded border p-2" />
                        </label>
                      </>
                    )}
                    {key === 'productFit' && (
                      <>
                        <label className="text-sm">Fit group
                          <select value={option.group}
                            onChange={event => updateOption(key, index, { group: event.target.value })}
                            className="mt-1 block w-full rounded border p-2">
                            <option value="category">Category</option>
                            <option value="demographic">Demographic</option>
                          </select>
                        </label>
                        <label className="text-sm">Match keywords (comma separated)
                          <input value={(option.keywords || []).join(', ')}
                            onChange={event => updateOption(key, index, { keywords: event.target.value.split(',').map(value => value.trim()).filter(Boolean) })}
                            className="mt-1 block w-full rounded border p-2" />
                        </label>
                      </>
                    )}
                    <button type="button" disabled={question.options.length <= 1}
                      onClick={() => removeOption(key, index)}
                      className="rounded border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50">
                      Remove choice
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" disabled={question.options.length >= 15}
                onClick={() => addOption(key)}
                className="rounded border px-3 py-2 text-sm text-indigo-700 disabled:opacity-50">
                Add button choice
              </button>
            </section>
          );
        })}

        <section className="space-y-3 rounded-lg border bg-white p-4">
          <h2 className="font-semibold">Season availability windows</h2>
          <p className="text-sm text-gray-600">All Season products are replenishable any time. Dated styles are offered only during these configured windows and while stock lasts.</p>
          <label className="block text-sm">Tenant time zone
            <input value={settings.seasonPolicy.timeZone}
              onChange={event => setSettings(current => ({ ...current, seasonPolicy: { ...current.seasonPolicy, timeZone: event.target.value } }))}
              className="mt-1 block w-full max-w-md rounded border p-2" />
          </label>
          <label className="block text-sm">All Season labels (comma separated)
            <input value={(settings.seasonPolicy.allSeasonLabels || []).join(', ')}
              onChange={event => setSettings(current => ({ ...current, seasonPolicy: { ...current.seasonPolicy, allSeasonLabels: event.target.value.split(',').map(value => value.trim()).filter(Boolean) } }))}
              className="mt-1 block w-full max-w-md rounded border p-2" />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {settings.seasonPolicy.seasons.map((season, index) => (
              <div key={season.name} className="rounded border p-3">
                <h3 className="font-medium">{season.name}</h3>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label className="text-sm">Starts (MM-DD)
                    <input value={season.start} onChange={event => updateSeason(index, { start: event.target.value })}
                      className="mt-1 block w-full rounded border p-2" />
                  </label>
                  <label className="text-sm">Ends (MM-DD)
                    <input value={season.end} onChange={event => updateSeason(index, { end: event.target.value })}
                      className="mt-1 block w-full rounded border p-2" />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 rounded-lg border bg-white p-4 sm:grid-cols-3">
          <label className="text-sm">Minimum units per style
            <input type="number" min="1" max="500" value={settings.defaults.minUnitsPerStyle}
              onChange={event => setSettings(current => ({ ...current, defaults: { ...current.defaults, minUnitsPerStyle: Number(event.target.value) } }))}
              className="mt-1 block w-full rounded border p-2" />
          </label>
          <label className="text-sm">Maximum quote lines
            <input type="number" min="1" max="60" value={settings.defaults.maxLines}
              onChange={event => setSettings(current => ({ ...current, defaults: { ...current.defaults, maxLines: Number(event.target.value) } }))}
              className="mt-1 block w-full rounded border p-2" />
          </label>
          <label className="text-sm">Minimum event lead time (days)
            <input type="number" min="0" max="180" value={settings.defaults.eventLeadDays}
              onChange={event => setSettings(current => ({ ...current, defaults: { ...current.defaults, eventLeadDays: Number(event.target.value) } }))}
              className="mt-1 block w-full rounded border p-2" />
          </label>
        </section>
        <button type="submit" disabled={saving}
          className="rounded bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">
          {saving ? 'Saving…' : 'Save quote settings'}
        </button>
      </form>
    </main>
  );
}

export { TIMING_KINDS, optionFor };
